import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { OktaService } from '../../services/okta/okta.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { Router } from '@angular/router';
import { catchError, Observable, throwError } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { BYPASS_AUTH_INTERCEPTOR } from '../../app.module';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  private readonly XSRF_COOKIE_NAME = 'XSRF-TOKEN';

  constructor(
    private oktaService: OktaService,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private router: Router,
    private cookieService: CookieService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Bypass if context flag is set
    if (request.context.get(BYPASS_AUTH_INTERCEPTOR)) {
      return next.handle(request);
    }

    const oktaToken = this.oktaService.getToken();

    // Handle JWT exchange endpoint separately
    if (request.url.includes(environment.STATEMENT_MESSAGING.JWT_EXCHANGE_TOKEN)) {
      return this.handleJwtExchange(request, next, oktaToken);
    }

    // For mutating requests, ensure we have OAuth token
    if (this.isMutatingRequest(request.method)) {
      return this.handleMutatingRequest(request, next, oktaToken);
    }

    // For read-only requests, use OKTA token
    return this.handleReadOnlyRequest(request, next, oktaToken);
  }

  private handleJwtExchange(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    oktaToken: string,
  ): Observable<HttpEvent<unknown>> {
    const modifiedRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${oktaToken}`,
      },
      withCredentials: true,
    });

    return next
      .handle(modifiedRequest)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  private handleMutatingRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    oktaToken: string,
  ): Observable<HttpEvent<unknown>> {
    const existingCaToken = this.oktaService.getCaToken();
    return this.sendRequestWithCaToken(request, next, existingCaToken);
  }

  private handleReadOnlyRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    oktaToken: string,
  ): Observable<HttpEvent<unknown>> {
    const modifiedRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${oktaToken}`,
      },
      withCredentials: true,
    });

    return next
      .handle(modifiedRequest)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  private sendRequestWithCaToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    caToken: string,
  ): Observable<HttpEvent<unknown>> {
    const crewId = this.oktaService.getCrewId();
    const modifiedRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${caToken}`,
        'X-Crew-Id': crewId,
      },
      withCredentials: true,
    });

    return next.handle(modifiedRequest);
  }

  private isMutatingRequest(method: string): boolean {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    if (err.status === 403) {
      this.cookieService.delete(this.XSRF_COOKIE_NAME, '/');
    }

    return throwError(() => err);
  }
}
